import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCodeBranch,
  faComments,
  faUser,
  faClock,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import FalconCloseButton from 'components/common/FalconCloseButton';
import AppContext from 'context/Context';
import { formatDateTime } from './TicketCustomerSidebar/utils';

const SelectTicketToMergeModal = ({
  show,
  onClose,
  client,
  currentTicket,
  openTickets = [],
  onSelectTicket
}) => {
  const context = useContext(AppContext);
  const isDark =
    context?.config?.isDark ??
    (typeof document !== 'undefined' &&
      (document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark')));

  if (!show) return null;

  const currentTicketId = currentTicket?.idTicket || currentTicket?.id || 'N/A';
  const clientId = client?.idClienteAkasia || client?.idCliente || client?.id || '';
  const clientName = client?.vNombreCompleto || client?.customerName || client?.nombre || 'el cliente';
  const clientDisplayName = clientId ? `#${clientId} - ${clientName}` : clientName;

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header className="border-bottom-0 pb-2 pt-3 px-4">
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: isDark ? 'rgba(79, 70, 229, 0.2)' : '#e0e7ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1rem'
            }}
          >
            <FontAwesomeIcon icon={faCodeBranch} />
          </div>
          <div>
            <h5 className="mb-0 fw-bold" style={{ color: isDark ? '#f8fafc' : '#0f172a' }}>
              Seleccionar Ticket a Combinar
            </h5>
            <p className="mb-0 fs--1" style={{ color: isDark ? '#94a3b8' : '#64748b' }}>
              El cliente <strong style={{ color: isDark ? '#f1f5f9' : '#1e293b' }}>{clientDisplayName}</strong> tiene {openTickets.length} tickets abiertos.
            </p>
          </div>
        </div>
        <FalconCloseButton onClick={onClose} className="ms-auto" />
      </Modal.Header>

      <Modal.Body className="pt-2 px-4 pb-3">
        <div
          className="p-2 mb-3 rounded-2"
          style={{
            backgroundColor: isDark ? 'rgba(59, 130, 246, 0.08)' : '#eff6ff',
            border: `1px solid ${isDark ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe'}`,
            fontSize: '0.80rem',
            color: isDark ? '#93c5fd' : '#1e40af'
          }}
        >
          Selecciona cuál de los siguientes tickets activos será el <strong>ticket principal</strong> que conservará la conversación del <strong>Ticket #{currentTicketId}</strong>:
        </div>

        <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
          {openTickets.map(item => {
            const ticketId = item.idTicket || item.id;
            const subject = item.vAsunto || 'Sin asunto registrado';
            const agent = (item.vNombreAgenteAsignado || '').trim();
            const phone = item.vTelefono || 'Sin teléfono';
            const isProgress = item.iEstadoTicket === 3;

            return (
              <div
                key={ticketId}
                className="p-3 rounded-3 transition-base"
                style={{
                  backgroundColor: isDark ? '#1e293b' : '#ffffff',
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.04)'
                }}
              >
                <div className="d-flex justify-content-between align-items-start mb-2 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-bold fs-0" style={{ color: '#4f46e5' }}>
                      #Ticket-{ticketId}
                    </span>
                    <span
                      className="badge rounded-pill"
                      style={{
                        backgroundColor: isProgress
                          ? (isDark ? 'rgba(16, 185, 129, 0.2)' : '#dcfce7')
                          : (isDark ? 'rgba(245, 158, 11, 0.2)' : '#fef3c7'),
                        color: isProgress
                          ? (isDark ? '#34d399' : '#15803d')
                          : (isDark ? '#fbbf24' : '#b45309'),
                        fontSize: '0.70rem'
                      }}
                    >
                      {isProgress ? 'En Progreso' : 'Abierto'}
                    </span>
                  </div>

                  <Button
                    variant="primary"
                    size="sm"
                    className="d-flex align-items-center gap-1"
                    style={{
                      backgroundColor: '#4f46e5',
                      borderColor: '#4f46e5',
                      fontSize: '0.78rem'
                    }}
                    onClick={() => onSelectTicket(item)}
                  >
                    <span>Combinar con este ticket</span>
                    <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.70rem' }} />
                  </Button>
                </div>

                <div className="mb-2">
                  <span className="d-block fw-semibold fs--1" style={{ color: isDark ? '#f1f5f9' : '#0f172a' }}>
                    {subject}
                  </span>
                </div>

                <div
                  className="row g-2 pt-2 border-top"
                  style={{
                    borderColor: isDark ? '#334155' : '#f1f5f9',
                    fontSize: '0.75rem',
                    color: isDark ? '#94a3b8' : '#64748b'
                  }}
                >
                  <div className="col-12 col-md-4 d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faComments} className="text-success" />
                    <span className="fw-medium" style={{ color: isDark ? '#cbd5e1' : '#334155' }}>
                      {phone}
                    </span>
                  </div>

                  <div className="col-12 col-md-4 d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faUser} style={{ color: isDark ? '#94a3b8' : '#64748b' }} />
                    <span>{agent ? agent : 'Sin agente asignado'}</span>
                  </div>

                  <div className="col-12 col-md-4 d-flex align-items-center gap-1">
                    <FontAwesomeIcon icon={faClock} className="text-warning" />
                    <span>{formatDateTime(item.dUltimaActividad || item.dCreatedDate)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top-0 pt-1 pb-3 px-4">
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

SelectTicketToMergeModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  client: PropTypes.object,
  currentTicket: PropTypes.object,
  openTickets: PropTypes.array,
  onSelectTicket: PropTypes.func.isRequired
};

export default SelectTicketToMergeModal;
